import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  discardFormCache,
  fetchFormCache,
  saveFormCache,
  startNewFormCache,
  WORK_ORDER_STOCK_TRANSFER_CACHE_PAYLOAD_VERSION,
  WORK_ORDER_STOCK_TRANSFER_CACHE_SCOPE,
} from '#/api/system/formCache';
import {
  buildLocalFormCacheKey,
  deleteLocalFormCacheRecord,
  getLocalFormCacheRecord,
  putLocalFormCacheRecord,
} from '#/utils/formCache';
import { toast } from '#/utils/toast';
import { getRequestErrorMessage } from '#/utils/requestError';

import useLeaveConfirm from '#/hooks/useLeaveConfirm';
import useConfirmDialog from '#/modules/commonComponents/dialog/useConfirmDialog';
import {
  buildCachePayloadFromForm,
  fromServerFormCachePayload,
  isMeaningfulCachePayload,
  isMeaningfulFormSnapshot,
  restoreFormFromPayload,
  stableStringifyCachePayload,
  toServerFormCachePayload,
  withLocalOnlyCacheFields,
} from '#/modules/momMgmt/workOrder/transfer/helper/formCachePayload';
import {
  FORM_CACHE_UI_STATUS,
  FORM_CACHE_UI_STATUS_LABEL,
} from '#/modules/momMgmt/workOrder/transfer/helper/formCacheUiStatus';

const AUTOSAVE_DEBOUNCE_MS = 2000;
const DEFAULT_API_ORIGIN_PATH = '/api';
const SYNC_FAILED_FALLBACK = '草稿同步失敗，請稍後再試';

function resolveApiOrigin() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${DEFAULT_API_ORIGIN_PATH}`;
  }
  return DEFAULT_API_ORIGIN_PATH;
}

function getErrorCode(error) {
  return error?.response?.data?.code || error?.code || '';
}

function getErrorMessage(error, fallback = SYNC_FAILED_FALLBACK) {
  return getRequestErrorMessage(error, fallback);
}

function buildPayloadFromFormSnapshot(snapshot) {
  return buildCachePayloadFromForm({
    workWarehouseId: snapshot?.targetWarehouseId,
    transferDate: snapshot?.transferDate,
    issueReason: snapshot?.issueReason,
    remark: snapshot?.remark,
    configMode: snapshot?.configMode,
    selectedWorkOrder: snapshot?.selectedWorkOrder,
    draftLines: snapshot?.draftLines,
  });
}

/**
 * 工單轉倉 Create Form Cache（Autosave／Reconcile／Submit lock）。
 * `enabled=false`（Edit）時完全 no-op。
 *
 * @param {object} options
 * @param {boolean} options.enabled
 * @param {() => object} options.getFormSnapshot
 * @param {(restored: object) => void} options.applyRestoredForm
 */
export function useWorkOrderTransferFormCache({
  enabled,
  getFormSnapshot,
  applyRestoredForm,
}) {
  const userId = useSelector((state) => state.infra.user?.id);
  const { confirm, confirmDialog } = useConfirmDialog();

  const applyRestoredFormRef = useRef(applyRestoredForm);
  const getFormSnapshotRef = useRef(getFormSnapshot);

  useEffect(() => {
    applyRestoredFormRef.current = applyRestoredForm;
  }, [applyRestoredForm]);

  useEffect(() => {
    getFormSnapshotRef.current = getFormSnapshot;
  }, [getFormSnapshot]);

  const [uiStatus, setUiStatus] = useState(FORM_CACHE_UI_STATUS.IDLE);
  const [syncFailedMessage, setSyncFailedMessage] = useState('');
  const [isHydrating, setIsHydrating] = useState(() => Boolean(enabled));
  const [autosaveStopped, setAutosaveStopped] = useState(false);

  const keyRef = useRef('');
  const serverRevisionRef = useRef(0);
  const serverSnapshotIdRef = useRef(null);
  const dirtyRef = useRef(false);
  const lastSyncedPayloadJsonRef = useRef(null);
  const latestPayloadRef = useRef(null);
  const putInFlightRef = useRef(false);
  const pendingAfterFlightRef = useRef(false);
  const debounceTimerRef = useRef(null);
  const autosaveStoppedRef = useRef(false);
  const hydrateDoneRef = useRef(false);
  const hydrateStartedRef = useRef(false);
  const resumePromptActiveRef = useRef(false);
  const submittedSuccessRef = useRef(false);
  const openConflictResolverRef = useRef(async () => {});
  const conflictPromptActiveRef = useRef(false);
  /** resume／本機回填後：捕捉 baseline，使用者真編輯前不 PUT。 */
  const skipAutosaveUntilEditRef = useRef(false);
  const resumeBaselineJsonRef = useRef(null);

  const setStopped = useCallback((stopped) => {
    autosaveStoppedRef.current = stopped;
    setAutosaveStopped(stopped);
  }, []);

  const armSkipAutosaveUntilEdit = useCallback((payload = null) => {
    skipAutosaveUntilEditRef.current = true;
    resumeBaselineJsonRef.current = payload != null
      ? stableStringifyCachePayload(payload)
      : null;
  }, []);

  const setStatus = useCallback((nextStatus, failedMessage = '') => {
    if (nextStatus === FORM_CACHE_UI_STATUS.SYNC_FAILED) {
      setSyncFailedMessage(failedMessage || SYNC_FAILED_FALLBACK);
    } else {
      setSyncFailedMessage('');
    }
    setUiStatus(nextStatus);
  }, []);

  const writeLocalRecord = useCallback(async ({
    payload,
    dirty,
    serverRevision,
    serverSnapshotId,
  }) => {
    const key = keyRef.current;
    if (!key) {
      return;
    }
    await putLocalFormCacheRecord({
      key,
      scope: WORK_ORDER_STOCK_TRANSFER_CACHE_SCOPE,
      payloadVersion: WORK_ORDER_STOCK_TRANSFER_CACHE_PAYLOAD_VERSION,
      payload,
      serverRevision,
      serverSnapshotId,
      dirty,
      localSavedAtEpochMs: Date.now(),
    });
  }, []);

  const runPut = async (payload, {
    foreground = false,
    openConflictDialog = true,
  } = {}) => {
    if (autosaveStoppedRef.current) {
      return { ok: false, reason: 'stopped' };
    }
    if (putInFlightRef.current) {
      pendingAfterFlightRef.current = true;
      return { ok: false, reason: 'in_flight' };
    }

    putInFlightRef.current = true;
    pendingAfterFlightRef.current = false;
    let shouldOpenConflict = false;
    if (!foreground) {
      setStatus(FORM_CACHE_UI_STATUS.SYNCING);
    }

    const expectedRevision = serverRevisionRef.current;
    const payloadJson = stableStringifyCachePayload(payload);
    const wirePayload = toServerFormCachePayload(payload);

    try {
      const result = await saveFormCache({
        expectedRevision,
        payloadVersion: WORK_ORDER_STOCK_TRANSFER_CACHE_PAYLOAD_VERSION,
        payload: wirePayload,
      });
      const entry = result?.data;
      if (!entry) {
        throw new Error('表單緩存儲存回應缺少 data');
      }

      serverRevisionRef.current = entry.revision;
      serverSnapshotIdRef.current = entry.snapshotId;

      const stillLatest = stableStringifyCachePayload(latestPayloadRef.current)
        === payloadJson;
      dirtyRef.current = !stillLatest;
      if (stillLatest) {
        lastSyncedPayloadJsonRef.current = payloadJson;
      }

      await writeLocalRecord({
        payload: latestPayloadRef.current ?? payload,
        dirty: dirtyRef.current,
        serverRevision: serverRevisionRef.current,
        serverSnapshotId: serverSnapshotIdRef.current,
      });

      if (entry.status === 'CONSUMED') {
        setStopped(true);
        setStatus(FORM_CACHE_UI_STATUS.CONSUMED);
        return { ok: false, reason: 'consumed', entry };
      }

      setStatus(
        dirtyRef.current
          ? FORM_CACHE_UI_STATUS.LOCAL_SAVED
          : FORM_CACHE_UI_STATUS.SERVER_SYNCED,
      );
      return { ok: true, entry };
    } catch (error) {
      const code = getErrorCode(error);
      if (code === 'FORM_CACHE_REVISION_CONFLICT') {
        setStatus(FORM_CACHE_UI_STATUS.CONFLICT);
        if (openConflictDialog) {
          shouldOpenConflict = true;
        }
        return { ok: false, reason: 'conflict', error };
      }
      if (code === 'FORM_CACHE_DRAFT_CONSUMED') {
        setStopped(true);
        setStatus(FORM_CACHE_UI_STATUS.CONSUMED);
        return { ok: false, reason: 'consumed', error };
      }
      if (code === 'FORM_CACHE_DRAFT_MISSING') {
        setStopped(true);
        const missingMsg = getErrorMessage(error, '草稿已不存在');
        setStatus(FORM_CACHE_UI_STATUS.SYNC_FAILED, missingMsg);
        toast.error(missingMsg);
        return { ok: false, reason: 'missing', error };
      }
      const failedMsg = getErrorMessage(error, SYNC_FAILED_FALLBACK);
      setStatus(FORM_CACHE_UI_STATUS.SYNC_FAILED, failedMsg);
      if (foreground) {
        toast.error(failedMsg);
      }
      return { ok: false, reason: 'error', error };
    } finally {
      putInFlightRef.current = false;
      if (shouldOpenConflict) {
        pendingAfterFlightRef.current = false;
        void openConflictResolverRef.current?.();
      } else if (
        pendingAfterFlightRef.current
        && dirtyRef.current
        && !autosaveStoppedRef.current
      ) {
        pendingAfterFlightRef.current = false;
        const next = latestPayloadRef.current;
        if (next) {
          debounceTimerRef.current = setTimeout(() => {
            runPut(next);
          }, AUTOSAVE_DEBOUNCE_MS);
        }
      }
    }
  };

  const runPutRef = useRef(runPut);
  useEffect(() => {
    runPutRef.current = runPut;
  });

  const scheduleDebouncedPut = useCallback(() => {
    if (autosaveStoppedRef.current || !enabled) {
      return;
    }
    if (skipAutosaveUntilEditRef.current) {
      return;
    }
    if (debounceTimerRef.current != null) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      if (skipAutosaveUntilEditRef.current) {
        return;
      }
      const payload = latestPayloadRef.current;
      if (payload) {
        runPutRef.current(payload);
      }
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [enabled]);

  const notifyFormChanged = useCallback(async () => {
    if (!enabled || !hydrateDoneRef.current || autosaveStoppedRef.current) {
      return;
    }
    const snapshot = getFormSnapshotRef.current();
    const payload = buildCachePayloadFromForm({
      workWarehouseId: snapshot.targetWarehouseId,
      transferDate: snapshot.transferDate,
      issueReason: snapshot.issueReason,
      remark: snapshot.remark,
      configMode: snapshot.configMode,
      selectedWorkOrder: snapshot.selectedWorkOrder,
      draftLines: snapshot.draftLines,
    });
    const payloadJson = stableStringifyCachePayload(payload);

    if (skipAutosaveUntilEditRef.current) {
      if (resumeBaselineJsonRef.current == null) {
        resumeBaselineJsonRef.current = payloadJson;
        latestPayloadRef.current = payload;
        return;
      }
      if (payloadJson === resumeBaselineJsonRef.current) {
        return;
      }
      skipAutosaveUntilEditRef.current = false;
      resumeBaselineJsonRef.current = null;
    }

    latestPayloadRef.current = payload;
    dirtyRef.current = true;
    try {
      await writeLocalRecord({
        payload,
        dirty: true,
        serverRevision: serverRevisionRef.current,
        serverSnapshotId: serverSnapshotIdRef.current,
      });
      setStatus(FORM_CACHE_UI_STATUS.LOCAL_SAVED);
      scheduleDebouncedPut();
    } catch (error) {
      const failedMsg = getErrorMessage(error, SYNC_FAILED_FALLBACK);
      setStatus(FORM_CACHE_UI_STATUS.SYNC_FAILED, failedMsg);
      toast.error(failedMsg);
    }
  }, [enabled, scheduleDebouncedPut, setStatus, writeLocalRecord]);

  const applyServerEntryToLocal = useCallback(async (entry, { dirty = false } = {}) => {
    const payload = fromServerFormCachePayload(entry?.payload) ?? {
      workWarehouseId: null,
      transferDate: '',
      issueReason: '',
      remark: '',
      configMode: 'byTargetWarehouse',
      selectedWorkOrder: null,
      lines: [],
    };
    serverRevisionRef.current = entry?.revision ?? 0;
    serverSnapshotIdRef.current = entry?.snapshotId ?? null;
    latestPayloadRef.current = payload;
    dirtyRef.current = dirty;
    if (!dirty) {
      lastSyncedPayloadJsonRef.current = stableStringifyCachePayload(payload);
    }
    await writeLocalRecord({
      payload,
      dirty,
      serverRevision: serverRevisionRef.current,
      serverSnapshotId: serverSnapshotIdRef.current,
    });
    const restored = restoreFormFromPayload(payload);
    if (restored) {
      armSkipAutosaveUntilEdit(payload);
      applyRestoredFormRef.current(restored);
    }
  }, [armSkipAutosaveUntilEdit, writeLocalRecord]);

  const applyLocalPayloadToForm = useCallback((localRecord) => {
    const restored = restoreFormFromPayload(localRecord.payload);
    if (restored) {
      armSkipAutosaveUntilEdit(localRecord.payload);
      applyRestoredFormRef.current(restored);
    }
    latestPayloadRef.current = localRecord.payload;
    serverRevisionRef.current = localRecord.serverRevision ?? 0;
    serverSnapshotIdRef.current = localRecord.serverSnapshotId ?? null;
    dirtyRef.current = Boolean(localRecord.dirty);
  }, [armSkipAutosaveUntilEdit]);

  const resolveConflict = useCallback(async ({ localRecord, serverEntry }) => {
    setStatus(FORM_CACHE_UI_STATUS.CONFLICT);
    const keepCurrent = await confirm({
      title: '草稿衝突',
      message: '草稿內容有衝突，請選擇要保留的版本',
      confirmText: '保留目前編輯',
      cancelText: '改用已暫存',
    });

    if (keepCurrent) {
      const latest = await fetchFormCache();
      if (latest?.data?.status === 'CONSUMED') {
        setStopped(true);
        setStatus(FORM_CACHE_UI_STATUS.CONSUMED);
        return;
      }
      serverRevisionRef.current = latest?.data?.revision ?? 0;
      serverSnapshotIdRef.current = latest?.data?.snapshotId ?? null;
      const snapshot = getFormSnapshotRef.current();
      const payload = localRecord?.payload
        ?? latestPayloadRef.current
        ?? buildCachePayloadFromForm({
          workWarehouseId: snapshot.targetWarehouseId,
          transferDate: snapshot.transferDate,
          issueReason: snapshot.issueReason,
          remark: snapshot.remark,
          configMode: snapshot.configMode,
          selectedWorkOrder: snapshot.selectedWorkOrder,
          draftLines: snapshot.draftLines,
        });
      latestPayloadRef.current = payload;
      dirtyRef.current = true;
      await writeLocalRecord({
        payload,
        dirty: true,
        serverRevision: serverRevisionRef.current,
        serverSnapshotId: serverSnapshotIdRef.current,
      });
      const putResult = await runPutRef.current(payload, {
        foreground: true,
        openConflictDialog: false,
      });
      if (putResult.reason === 'conflict') {
        toast.error('仍有版本衝突，請重新選擇版本');
      }
      return;
    }

    if (serverEntry) {
      await applyServerEntryToLocal(serverEntry, { dirty: false });
      setStatus(FORM_CACHE_UI_STATUS.SERVER_SYNCED);
    }
  }, [applyServerEntryToLocal, confirm, setStopped, setStatus, writeLocalRecord]);

  /** PUT 409／狀態列「選擇版本」：GET 最新後開 resolveConflict。 */
  const openConflictResolver = useCallback(async () => {
    if (conflictPromptActiveRef.current) {
      return;
    }
    conflictPromptActiveRef.current = true;
    setStatus(FORM_CACHE_UI_STATUS.CONFLICT);
    try {
      let localRecord = null;
      try {
        localRecord = keyRef.current
          ? await getLocalFormCacheRecord(keyRef.current)
          : null;
      } catch {
        localRecord = null;
      }
      const snapshot = getFormSnapshotRef.current?.();
      const currentPayload = latestPayloadRef.current
        ?? (snapshot
          ? buildCachePayloadFromForm({
            workWarehouseId: snapshot.targetWarehouseId,
            transferDate: snapshot.transferDate,
            issueReason: snapshot.issueReason,
            remark: snapshot.remark,
            configMode: snapshot.configMode,
            selectedWorkOrder: snapshot.selectedWorkOrder,
            draftLines: snapshot.draftLines,
          })
          : null);
      if (currentPayload) {
        localRecord = {
          ...(localRecord || {}),
          payload: currentPayload,
          dirty: true,
          serverRevision: localRecord?.serverRevision ?? serverRevisionRef.current,
          serverSnapshotId:
            localRecord?.serverSnapshotId ?? serverSnapshotIdRef.current,
        };
      }

      let serverEntry = null;
      try {
        const response = await fetchFormCache();
        serverEntry = response?.data ?? null;
      } catch (error) {
        toast.error(getErrorMessage(error, '讀取草稿失敗'));
        return;
      }
      await resolveConflict({ localRecord, serverEntry });
    } finally {
      conflictPromptActiveRef.current = false;
    }
  }, [resolveConflict, setStatus]);

  useEffect(() => {
    openConflictResolverRef.current = openConflictResolver;
  }, [openConflictResolver]);

  /**
   * POST start-new。silent：hydrate 代勞時不 toast，失敗也不開衝突 dialog。
   * @returns {Promise<{ ok: boolean, reason?: string, error?: unknown, entry?: object }>}
   */
  const runStartNew = useCallback(async (payload, { silent = false } = {}) => {
    try {
      const result = await startNewFormCache({
        payloadVersion: WORK_ORDER_STOCK_TRANSFER_CACHE_PAYLOAD_VERSION,
        payload: toServerFormCachePayload(payload),
      });
      const entry = result?.data;
      if (!entry) {
        throw new Error('建立新草稿失敗');
      }
      setStopped(false);
      await applyServerEntryToLocal({
        ...entry,
        payload: withLocalOnlyCacheFields(entry.payload, payload),
      }, { dirty: false });
      setStatus(FORM_CACHE_UI_STATUS.SERVER_SYNCED);
      if (!silent) {
        toast.success('已建立新草稿');
      }
      return { ok: true, entry };
    } catch (error) {
      if (getErrorCode(error) === 'FORM_CACHE_REVISION_CONFLICT') {
        if (!silent) {
          const latest = await fetchFormCache();
          await resolveConflict({
            localRecord: { payload, dirty: true },
            serverEntry: latest?.data,
          });
        }
        return { ok: false, reason: 'conflict', error };
      }
      if (!silent) {
        toast.error(getErrorMessage(error, '建立新草稿失敗'));
      }
      return { ok: false, reason: 'error', error };
    }
  }, [applyServerEntryToLocal, resolveConflict, setStopped, setStatus]);

  const runStartNewRef = useRef(runStartNew);
  useEffect(() => {
    runStartNewRef.current = runStartNew;
  });

  const handleStartNew = useCallback(async () => {
    const snapshot = getFormSnapshotRef.current();
    await runStartNew(buildPayloadFromFormSnapshot(snapshot), { silent: false });
  }, [runStartNew]);

  const handleDiscardServer = useCallback(async (snapshotId) => {
    if (!snapshotId) {
      return;
    }
    try {
      await discardFormCache(snapshotId);
      if (keyRef.current) {
        await deleteLocalFormCacheRecord(keyRef.current);
      }
      serverRevisionRef.current = 0;
      serverSnapshotIdRef.current = null;
      dirtyRef.current = false;
      latestPayloadRef.current = null;
      lastSyncedPayloadJsonRef.current = null;
      setStatus(FORM_CACHE_UI_STATUS.IDLE);
    } catch (error) {
      if (getErrorCode(error) === 'FORM_CACHE_SNAPSHOT_CONFLICT') {
        toast.error('草稿已有更新版本，無法捨棄');
        const latest = await fetchFormCache();
        if (latest?.data) {
          await resolveConflict({
            localRecord: await getLocalFormCacheRecord(keyRef.current),
            serverEntry: latest.data,
          });
        }
        return;
      }
      toast.error(getErrorMessage(error, '捨棄草稿失敗'));
    }
  }, [resolveConflict, setStatus]);

  /** 繼續編輯：dirty 優先本機；否則依 reconcile（同 snapshot／衝突）。 */
  const continueFromCache = useCallback(async (localRecord, serverEntry) => {
    const hasLocal = Boolean(localRecord?.payload);
    const localDirty = Boolean(localRecord?.dirty);
    const serverActive = serverEntry?.status === 'ACTIVE';

    if (hasLocal && localDirty) {
      if (serverActive) {
        const sameSnapshot = localRecord.serverSnapshotId
          && localRecord.serverSnapshotId === serverEntry.snapshotId;
        if (!sameSnapshot) {
          await resolveConflict({ localRecord, serverEntry });
          return;
        }
      }
      applyLocalPayloadToForm(localRecord);
      setStatus(FORM_CACHE_UI_STATUS.LOCAL_SAVED);
      return;
    }

    if (hasLocal && serverActive) {
      const sameSnapshot = localRecord.serverSnapshotId
        && localRecord.serverSnapshotId === serverEntry.snapshotId;
      if (sameSnapshot) {
        await applyServerEntryToLocal(serverEntry, { dirty: false });
        setStatus(FORM_CACHE_UI_STATUS.SERVER_SYNCED);
        return;
      }
      await resolveConflict({ localRecord, serverEntry });
      return;
    }

    if (hasLocal) {
      applyLocalPayloadToForm(localRecord);
      setStatus(FORM_CACHE_UI_STATUS.LOCAL_SAVED);
      return;
    }

    if (serverActive) {
      await applyServerEntryToLocal(serverEntry, { dirty: false });
      setStatus(FORM_CACHE_UI_STATUS.SERVER_SYNCED);
    }
  }, [applyLocalPayloadToForm, applyServerEntryToLocal, resolveConflict, setStatus]);

  /** 重新編輯：discard server（若有）＋清 IDB；不套用舊 payload（避免閃舊資料）。 */
  const restartFreshEditing = useCallback(async (serverEntry) => {
    if (serverEntry?.status === 'ACTIVE' && serverEntry.snapshotId) {
      await handleDiscardServer(serverEntry.snapshotId);
    } else if (keyRef.current) {
      try {
        await deleteLocalFormCacheRecord(keyRef.current);
      } catch {
        // ignore
      }
      serverRevisionRef.current = 0;
      serverSnapshotIdRef.current = null;
      dirtyRef.current = false;
      latestPayloadRef.current = null;
      lastSyncedPayloadJsonRef.current = null;
      setStatus(FORM_CACHE_UI_STATUS.IDLE);
    }
  }, [handleDiscardServer, setStatus]);

  useEffect(() => {
    if (!enabled || !userId) {
      return undefined;
    }
    if (hydrateStartedRef.current) {
      return undefined;
    }
    hydrateStartedRef.current = true;

    let cancelled = false;

    async function hydrate() {
      setIsHydrating(true);
      const key = buildLocalFormCacheKey({
        apiOrigin: resolveApiOrigin(),
        userId,
        scope: WORK_ORDER_STOCK_TRANSFER_CACHE_SCOPE,
      });
      keyRef.current = key;

      let localRecord;
      let serverEntry;
      try {
        localRecord = await getLocalFormCacheRecord(key);
      } catch {
        localRecord = null;
      }
      if (localRecord?.payload && !isMeaningfulCachePayload(localRecord.payload)) {
        try {
          await deleteLocalFormCacheRecord(key);
        } catch {
          // ignore
        }
        localRecord = null;
      }
      try {
        const response = await fetchFormCache();
        serverEntry = response?.data ?? null;
      } catch (error) {
        toast.error(getErrorMessage(error, '讀取草稿失敗'));
        if (cancelled) {
          return;
        }
        if (localRecord?.payload && isMeaningfulCachePayload(localRecord.payload)) {
          resumePromptActiveRef.current = true;
          setStatus(FORM_CACHE_UI_STATUS.RESTORE_AVAILABLE);
          const continueEdit = await confirm({
            title: '未送出的表單',
            message: '您上次有未送出的表單資料，是否要繼續編輯？',
            confirmText: '繼續編輯',
            cancelText: '重新編輯',
          });
          resumePromptActiveRef.current = false;
          if (cancelled) {
            return;
          }
          if (continueEdit) {
            applyLocalPayloadToForm(localRecord);
            setStatus(FORM_CACHE_UI_STATUS.LOCAL_SAVED);
          } else {
            await restartFreshEditing(null);
          }
        }
        hydrateDoneRef.current = true;
        setIsHydrating(false);
        return;
      }

      if (cancelled) {
        return;
      }

      const hasLocal = Boolean(localRecord?.payload);
      const serverStatus = serverEntry?.status;
      const serverMeaningful = serverStatus === 'ACTIVE'
        && isMeaningfulCachePayload(serverEntry?.payload);
      const localMeaningful = hasLocal
        && isMeaningfulCachePayload(localRecord.payload);

      if (serverStatus === 'CONSUMED') {
        async function fallBackConsumedUi() {
          setStopped(true);
          setStatus(FORM_CACHE_UI_STATUS.CONSUMED);
        }

        if (localMeaningful && localRecord?.dirty) {
          resumePromptActiveRef.current = true;
          setStatus(FORM_CACHE_UI_STATUS.RESTORE_AVAILABLE);
          const continueEdit = await confirm({
            title: '未送出的表單',
            message: '您上次有未送出的表單資料，是否要繼續編輯？',
            confirmText: '繼續編輯',
            cancelText: '重新編輯',
          });
          resumePromptActiveRef.current = false;
          if (cancelled) {
            return;
          }
          if (continueEdit) {
            applyLocalPayloadToForm(localRecord);
            const startResult = await runStartNewRef.current(
              localRecord.payload,
              { silent: true },
            );
            if (!startResult.ok) {
              await fallBackConsumedUi();
            }
          } else {
            await restartFreshEditing(null);
            const freshPayload = buildPayloadFromFormSnapshot(
              getFormSnapshotRef.current?.() ?? {},
            );
            const startResult = await runStartNewRef.current(freshPayload, {
              silent: true,
            });
            if (!startResult.ok) {
              await fallBackConsumedUi();
            }
          }
        } else {
          if (hasLocal) {
            try {
              await deleteLocalFormCacheRecord(key);
            } catch {
              // ignore
            }
          }
          const freshPayload = buildPayloadFromFormSnapshot(
            getFormSnapshotRef.current?.() ?? {},
          );
          const startResult = await runStartNewRef.current(freshPayload, {
            silent: true,
          });
          if (!startResult.ok) {
            await fallBackConsumedUi();
          }
        }
        hydrateDoneRef.current = true;
        setIsHydrating(false);
        return;
      }

      if (!localMeaningful && !serverMeaningful) {
        if (hasLocal && !localMeaningful) {
          try {
            await deleteLocalFormCacheRecord(key);
          } catch {
            // ignore
          }
        }
        hydrateDoneRef.current = true;
        setIsHydrating(false);
        return;
      }

      resumePromptActiveRef.current = true;
      setStatus(FORM_CACHE_UI_STATUS.RESTORE_AVAILABLE);
      const continueEdit = await confirm({
        title: '未送出的表單',
        message: '您上次有未送出的表單資料，是否要繼續編輯？',
        confirmText: '繼續編輯',
        cancelText: '重新編輯',
      });
      resumePromptActiveRef.current = false;
      if (cancelled) {
        return;
      }
      if (continueEdit) {
        await continueFromCache(localRecord, serverEntry);
      } else {
        await restartFreshEditing(serverEntry);
      }
      hydrateDoneRef.current = true;
      setIsHydrating(false);
    }

    hydrate();

    return () => {
      cancelled = true;
      if (debounceTimerRef.current != null) {
        clearTimeout(debounceTimerRef.current);
      }
    };
    // 僅在 enabled／userId 就緒時 hydrate 一次；其餘以 ref 取最新回呼。
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional once-per-session hydrate
  }, [enabled, userId]);

  const shouldBlockLeave = useCallback(({ currentLocation, nextLocation }) => {
    if (!enabled || !userId) {
      return false;
    }
    if (!hydrateDoneRef.current || isHydrating) {
      return false;
    }
    if (resumePromptActiveRef.current) {
      return false;
    }
    if (conflictPromptActiveRef.current) {
      return false;
    }
    if (submittedSuccessRef.current) {
      return false;
    }
    if (
      currentLocation.pathname === nextLocation.pathname
      && currentLocation.search === nextLocation.search
    ) {
      return false;
    }
    const snapshot = getFormSnapshotRef.current?.();
    return isMeaningfulFormSnapshot(snapshot);
  }, [enabled, isHydrating, userId]);

  useLeaveConfirm({
    when: shouldBlockLeave,
    confirm,
    title: '離開頁面',
    message: '表單尚未送出，確定要離開？',
    confirmText: '確定離開',
    cancelText: '取消',
  });

  const flushForSubmit = useCallback(async () => {
    if (!enabled) {
      return { ok: true, formCacheSnapshotId: null };
    }
    if (autosaveStoppedRef.current) {
      toast.error('草稿已消耗或已停止同步，請先建立新草稿');
      return { ok: false };
    }
    if (debounceTimerRef.current != null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const snapshot = getFormSnapshotRef.current();
    const payload = buildCachePayloadFromForm({
      workWarehouseId: snapshot.targetWarehouseId,
      transferDate: snapshot.transferDate,
      issueReason: snapshot.issueReason,
      remark: snapshot.remark,
      configMode: snapshot.configMode,
      selectedWorkOrder: snapshot.selectedWorkOrder,
      draftLines: snapshot.draftLines,
    });
    latestPayloadRef.current = payload;

    if (dirtyRef.current) {
      const putResult = await runPutRef.current(payload, { foreground: true });
      if (!putResult.ok) {
        toast.error('正式送出前同步失敗，請處理衝突後再試');
        return { ok: false };
      }
    }

    if (dirtyRef.current) {
      toast.error('仍有未同步變更，無法送出');
      return { ok: false };
    }

    const currentJson = stableStringifyCachePayload(payload);
    if (currentJson !== lastSyncedPayloadJsonRef.current) {
      toast.error('快照與目前表單不一致，請稍候同步後再送出');
      return { ok: false };
    }

    const snapshotId = serverSnapshotIdRef.current;
    if (!snapshotId) {
      toast.error('缺少草稿快照，無法送出');
      return { ok: false };
    }

    return { ok: true, formCacheSnapshotId: snapshotId, payload };
  }, [enabled]);

  const onCreateSuccess = useCallback(async () => {
    submittedSuccessRef.current = true;
    if (debounceTimerRef.current != null) {
      clearTimeout(debounceTimerRef.current);
    }
    setStopped(true);
    if (keyRef.current) {
      try {
        await deleteLocalFormCacheRecord(keyRef.current);
      } catch {
        // ignore
      }
    }
    serverRevisionRef.current = 0;
    serverSnapshotIdRef.current = null;
    dirtyRef.current = false;
    latestPayloadRef.current = null;
    lastSyncedPayloadJsonRef.current = null;
    setStatus(FORM_CACHE_UI_STATUS.IDLE);
  }, [setStopped, setStatus]);

  const markInvalidAfterRestore = useCallback(() => {
    setStatus(FORM_CACHE_UI_STATUS.INVALID_AFTER_RESTORE);
  }, [setStatus]);

  const uiStatusLabel = uiStatus === FORM_CACHE_UI_STATUS.SYNC_FAILED
    ? (syncFailedMessage || FORM_CACHE_UI_STATUS_LABEL[FORM_CACHE_UI_STATUS.SYNC_FAILED])
    : (FORM_CACHE_UI_STATUS_LABEL[uiStatus] || '');

  return {
    enabled: Boolean(enabled && userId),
    isHydrating: Boolean(enabled && userId && isHydrating),
    uiStatus,
    uiStatusLabel,
    autosaveStopped,
    confirmDialog,
    notifyFormChanged,
    flushForSubmit,
    onCreateSuccess,
    handleStartNew,
    handleResolveConflict: openConflictResolver,
    markInvalidAfterRestore,
  };
}

export default useWorkOrderTransferFormCache;
